import {
    searchPrefetchObservationSchema,
    searchPrefetchSourceSchema,
    type SearchPrefetchObservation,
    type SearchPrefetchSource,
} from "@wags/discovery/search/search-prefetch.contract.ts";

export const searchPrefetchSources = searchPrefetchSourceSchema.options;

export type { SearchPrefetchSource };

export const searchPrefetchObservations =
    searchPrefetchObservationSchema.options;

export type { SearchPrefetchObservation };

export type SearchPrefetchAttemptSummary = {
    attemptId: string;
    searchKeyId: string;
    source: SearchPrefetchSource;
    observations: SearchPrefetchObservation[];
    networkDurationMs?: number;
    networkDurationCensored?: boolean;
    readyAgeMs?: number;
    prefetchedResultCount?: number;
    displayedResultCount?: number;
    hydratedResultCount?: number;
};

export type SearchPrefetchAttemptHandle = {
    attemptId: string;
    key: string;
};

export type SearchPrefetchSessionSummary = {
    schemaVersion: 1;
    windowSequence: number;
    pageHidePersisted: boolean;
    pageHideCount: number;
    durationMs: number;
    attemptBufferLimit: number;
    attemptsDropped: number;
    attemptTransitionsDropped: number;
    searchKeysDropped: number;
    adoptionsDropped: number;
    estimatedPropertiesBytes: number;
    completedWithoutDisplayAtPagehide: Record<SearchPrefetchSource, number>;
    completedNotAdoptedAtPagehide: Record<SearchPrefetchSource, number>;
    bySource: Record<
        SearchPrefetchSource,
        Record<SearchPrefetchObservation, number>
    >;
};

type PrefetchAttempt = SearchPrefetchAttemptSummary & {
    key: string;
    startedAt: number;
    readyAt?: number;
    completedWindowSequence?: number;
    networkInitiated: boolean;
    adopted: boolean;
};

type PrefetchAdoption = {
    searchKeyId: string;
    dataUpdatedAt: number;
    readyAgeMs: number;
};

const MAX_ATTEMPT_DETAILS = 32;
const MAX_SEARCH_KEYS = 64;
const MAX_ATTEMPTS_PER_PRESENTATION = 12;
const MAX_ADOPTION_KEYS = 32;
const PREFETCH_STALE_TIME_MS = 5 * 60 * 1_000;

function emptySourceCounts(): Record<
    SearchPrefetchSource,
    Record<SearchPrefetchObservation, number>
> {
    return Object.fromEntries(
        searchPrefetchSources.map((source) => [
            source,
            Object.fromEntries(
                searchPrefetchObservations.map((observation) => [
                    observation,
                    0,
                ]),
            ),
        ]),
    ) as Record<
        SearchPrefetchSource,
        Record<SearchPrefetchObservation, number>
    >;
}

function createId() {
    return crypto.randomUUID();
}

function evictOldest<K, V>(map: Map<K, V>) {
    const entries = [...map.entries()];
    if (entries.length === 0) return false;
    map.clear();
    for (const [key, value] of entries.slice(1)) map.set(key, value);
    return true;
}

export class SearchPrefetchTracker {
    private readonly keyIds = new Map<string, string>();
    private readonly attempts: PrefetchAttempt[] = [];
    private readonly attemptById = new Map<string, PrefetchAttempt>();
    private readonly overflowById = new Map<
        string,
        {
            attemptId: string;
            source: SearchPrefetchSource;
            key: string;
            searchKeyId: string;
            startedAt: number;
            readyAt?: number;
            completedWindowSequence?: number;
            networkInitiated: boolean;
            adopted: boolean;
            observations: Set<SearchPrefetchObservation>;
        }
    >();
    private readonly adoptions = new Map<
        string,
        Map<string, PrefetchAdoption>
    >();
    private counts = emptySourceCounts();
    private windowStartedAt: number;
    private windowSequence = 0;
    private pageHideCount = 0;
    private attemptsDropped = 0;
    private attemptTransitionsDropped = 0;
    private searchKeysDropped = 0;
    private adoptionsDropped = 0;

    constructor(
        private readonly now: () => number = Date.now,
        private readonly makeId: () => string = createId,
    ) {
        this.windowStartedAt = this.now();
    }

    beginAttempt(source: SearchPrefetchSource, key: string) {
        this.expireUnused();
        let searchKeyId = this.keyIds.get(key);
        if (!searchKeyId) {
            if (this.keyIds.size >= MAX_SEARCH_KEYS) {
                if (evictOldest(this.keyIds)) this.searchKeysDropped += 1;
            }
            searchKeyId = this.makeId();
            this.keyIds.set(key, searchKeyId);
        }

        this.count(source, "attempted");
        if (this.attempts.length >= MAX_ATTEMPT_DETAILS) {
            this.attemptsDropped += 1;
            const attemptId = this.makeId();
            if (this.overflowById.size < 128) {
                this.overflowById.set(attemptId, {
                    attemptId,
                    source,
                    key,
                    searchKeyId,
                    startedAt: this.now(),
                    networkInitiated: false,
                    adopted: false,
                    observations: new Set(),
                });
            } else {
                this.attemptTransitionsDropped += 1;
            }
            return { attemptId, key, searchKeyId };
        }

        const attempt: PrefetchAttempt = {
            attemptId: this.makeId(),
            searchKeyId,
            source,
            observations: ["attempted"],
            key,
            startedAt: this.now(),
            networkInitiated: false,
            adopted: false,
        };
        this.attempts.push(attempt);
        this.attemptById.set(attempt.attemptId, attempt);
        return { attemptId: attempt.attemptId, key, searchKeyId };
    }

    startAttempt(
        handle: SearchPrefetchAttemptHandle,
        state: {
            hasData: boolean;
            dataUpdatedAt: number;
            isFetching: boolean;
        },
    ) {
        const attempt = this.attemptById.get(handle.attemptId);
        const overflowAttempt = this.overflowById.get(handle.attemptId);
        if (!attempt && !overflowAttempt) return;
        const now = this.now();
        const fresh =
            state.hasData && now - state.dataUpdatedAt < PREFETCH_STALE_TIME_MS;
        const existingForKey = this.attempts.filter(
            (candidate) => candidate.key === handle.key,
        );
        const hasOtherAttempt =
            existingForKey.some(
                (candidate) => candidate.attemptId !== handle.attemptId,
            ) ||
            [...this.overflowById.values()].some(
                (candidate) =>
                    candidate.key === handle.key &&
                    candidate.attemptId !== handle.attemptId,
            );

        if (state.isFetching && !state.hasData) {
            if (attempt) {
                this.observe(attempt, "inflight_joined");
                this.observe(attempt, "dedup_reuse");
            } else if (overflowAttempt) {
                this.observeOverflow(overflowAttempt, "inflight_joined");
                this.observeOverflow(overflowAttempt, "dedup_reuse");
            }
            return;
        }
        if (fresh) {
            if (attempt) {
                this.observe(attempt, "ready_hit");
                if (hasOtherAttempt) this.observe(attempt, "dedup_reuse");
                attempt.readyAt = state.dataUpdatedAt;
                attempt.readyAgeMs = Math.max(0, now - state.dataUpdatedAt);
            } else if (overflowAttempt) {
                this.observeOverflow(overflowAttempt, "ready_hit");
                if (hasOtherAttempt)
                    this.observeOverflow(overflowAttempt, "dedup_reuse");
                overflowAttempt.readyAt = state.dataUpdatedAt;
            }
            return;
        }
        if (state.hasData) {
            if (attempt) {
                this.observe(attempt, "stale_data_reused");
                attempt.readyAt = state.dataUpdatedAt;
                attempt.readyAgeMs = Math.max(0, now - state.dataUpdatedAt);
            } else if (overflowAttempt) {
                this.observeOverflow(overflowAttempt, "stale_data_reused");
                overflowAttempt.readyAt = state.dataUpdatedAt;
            }
        }
    }

    networkStarted(attemptId: string) {
        const attempt = this.attemptById.get(attemptId);
        const overflowAttempt = this.overflowById.get(attemptId);
        if (!attempt && !overflowAttempt) return;
        if (attempt?.networkInitiated || overflowAttempt?.networkInitiated)
            return;
        if (attempt) {
            attempt.networkInitiated = true;
            attempt.startedAt = this.now();
            this.observe(attempt, "network_initiated");
        } else if (overflowAttempt) {
            overflowAttempt.networkInitiated = true;
            overflowAttempt.startedAt = this.now();
            this.observeOverflow(overflowAttempt, "network_initiated");
        }
    }

    resolveAttempt(
        attemptId: string,
        result: {
            resultCount: number;
            finishedAt?: number;
            dataUpdatedAt?: number;
        },
    ) {
        const attempt = this.attemptById.get(attemptId);
        const overflowAttempt = this.overflowById.get(attemptId);
        if (!attempt && !overflowAttempt) return;
        const finishedAt = result.finishedAt ?? this.now();
        const networkInitiated =
            attempt?.networkInitiated ?? overflowAttempt?.networkInitiated;
        if (networkInitiated) {
            if (attempt) {
                this.observe(attempt, "network_completed");
                const durationMs = Math.max(0, finishedAt - attempt.startedAt);
                attempt.networkDurationCensored = durationMs > 600_000;
                attempt.networkDurationMs = Math.min(durationMs, 600_000);
                attempt.completedWindowSequence = this.windowSequence;
            } else if (overflowAttempt) {
                this.observeOverflow(overflowAttempt, "network_completed");
                overflowAttempt.readyAt = result.dataUpdatedAt ?? finishedAt;
                overflowAttempt.completedWindowSequence = this.windowSequence;
            }
        }
        if (attempt) {
            attempt.prefetchedResultCount = result.resultCount;
            attempt.readyAt ??= result.dataUpdatedAt ?? finishedAt;
        } else if (overflowAttempt) {
            overflowAttempt.readyAt ??= result.dataUpdatedAt ?? finishedAt;
        }
    }

    cancelAttempt(attemptId: string) {
        const attempt = this.attemptById.get(attemptId);
        if (attempt) this.observe(attempt, "cancelled");
        const overflowAttempt = this.overflowById.get(attemptId);
        if (overflowAttempt) this.observeOverflow(overflowAttempt, "cancelled");
    }

    failAttempt(attemptId: string) {
        const attempt = this.attemptById.get(attemptId);
        if (attempt) this.observe(attempt, "error");
        const overflowAttempt = this.overflowById.get(attemptId);
        if (overflowAttempt) this.observeOverflow(overflowAttempt, "error");
    }

    adoptWarmCache(key: string, submissionId: string, dataUpdatedAt: number) {
        const searchKeyId = this.keyIds.get(key);
        if (!searchKeyId) return;
        const readyAgeMs = Math.max(0, this.now() - dataUpdatedAt);
        if (readyAgeMs >= PREFETCH_STALE_TIME_MS) return;
        if (
            !this.adoptions.has(key) &&
            this.adoptions.size >= MAX_ADOPTION_KEYS
        ) {
            if (evictOldest(this.adoptions)) {
                this.adoptionsDropped += 1;
            }
        }
        const bySubmission = this.adoptions.get(key) ?? new Map();
        if (!bySubmission.has(submissionId) && bySubmission.size >= 8) {
            if (evictOldest(bySubmission)) {
                this.adoptionsDropped += 1;
            }
        }
        bySubmission.set(submissionId, {
            searchKeyId,
            dataUpdatedAt,
            readyAgeMs,
        });
        for (const attempt of this.attempts) {
            if (
                attempt.key === key &&
                attempt.searchKeyId === searchKeyId &&
                attempt.readyAt === dataUpdatedAt
            ) {
                attempt.adopted = true;
            }
        }
        for (const attempt of this.overflowById.values()) {
            if (
                attempt.key === key &&
                attempt.searchKeyId === searchKeyId &&
                attempt.readyAt === dataUpdatedAt
            ) {
                attempt.adopted = true;
            }
        }
        this.adoptions.set(key, bySubmission);
    }

    recordDisplayed(
        key: string,
        submissionId: string,
        resultCount: number,
        hydratedResultCount: number,
    ) {
        const adoption = this.adoptions.get(key)?.get(submissionId);
        if (!adoption) return { attempts: [], dropped: 0 };
        const matching = this.attempts.filter(
            (attempt) =>
                attempt.key === key &&
                attempt.searchKeyId === adoption.searchKeyId &&
                attempt.readyAt === adoption.dataUpdatedAt,
        );
        const matchingOverflow = [...this.overflowById.values()].filter(
            (attempt) =>
                attempt.key === key &&
                attempt.searchKeyId === adoption.searchKeyId &&
                attempt.readyAt === adoption.dataUpdatedAt,
        );
        const detailAttempts = matching.slice(0, MAX_ATTEMPTS_PER_PRESENTATION);
        const allMatching = [
            ...matching.map((attempt) => ({
                source: attempt.source,
                observations: attempt.observations,
                observe: (observation: SearchPrefetchObservation) =>
                    this.observe(attempt, observation),
                attempt,
            })),
            ...matchingOverflow.map((attempt) => ({
                source: attempt.source,
                observations: [...attempt.observations],
                observe: (observation: SearchPrefetchObservation) =>
                    this.observeOverflow(attempt, observation),
                attempt,
            })),
        ];
        const networkOwner = allMatching.find(
            ({ observations }) =>
                observations.includes("network_initiated") &&
                observations.includes("network_completed") &&
                !observations.includes("error") &&
                !observations.includes("cancelled") &&
                !observations.includes("expired_unused"),
        );
        const cacheOwner = allMatching.find(
            ({ observations }) =>
                observations.includes("ready_hit") &&
                !observations.includes("dedup_reuse"),
        );
        const displayOwner = networkOwner ?? cacheOwner;
        if (displayOwner) {
            displayOwner.observe("displayed");
        }
        return {
            attempts: detailAttempts.map((attempt) => ({
                attemptId: attempt.attemptId,
                searchKeyId: attempt.searchKeyId,
                source: attempt.source,
                observations: [...attempt.observations],
                ...(attempt.networkDurationMs === undefined
                    ? {}
                    : {
                          networkDurationMs: attempt.networkDurationMs,
                          ...(attempt.networkDurationCensored
                              ? { networkDurationCensored: true }
                              : {}),
                      }),
                readyAgeMs: adoption.readyAgeMs,
                prefetchedResultCount:
                    attempt.prefetchedResultCount ?? resultCount,
                ...(attempt === displayOwner?.attempt
                    ? {
                          displayedResultCount: resultCount,
                          hydratedResultCount,
                      }
                    : {}),
            })),
            dropped: Math.max(
                0,
                matching.length +
                    matchingOverflow.length -
                    detailAttempts.length,
            ),
        };
    }

    pageHideSummary(persisted: boolean) {
        this.expireUnused();
        this.pageHideCount += 1;
        const durationMs = Math.max(0, this.now() - this.windowStartedAt);
        const completedWithoutDisplayAtPagehide = Object.fromEntries(
            searchPrefetchSources.map((source) => [source, 0]),
        ) as Record<SearchPrefetchSource, number>;
        const completedNotAdoptedAtPagehide = Object.fromEntries(
            searchPrefetchSources.map((source) => [source, 0]),
        ) as Record<SearchPrefetchSource, number>;
        for (const attempt of this.attempts) {
            if (
                attempt.completedWindowSequence !== this.windowSequence ||
                !attempt.observations.includes("network_completed")
            ) {
                continue;
            }
            if (!attempt.observations.includes("displayed")) {
                completedWithoutDisplayAtPagehide[attempt.source] += 1;
            }
            if (!attempt.adopted) {
                completedNotAdoptedAtPagehide[attempt.source] += 1;
            }
        }
        for (const attempt of this.overflowById.values()) {
            if (
                attempt.completedWindowSequence !== this.windowSequence ||
                !attempt.observations.has("network_completed")
            ) {
                continue;
            }
            if (!attempt.observations.has("displayed")) {
                completedWithoutDisplayAtPagehide[attempt.source] += 1;
            }
            if (!attempt.adopted) {
                completedNotAdoptedAtPagehide[attempt.source] += 1;
            }
        }
        const base = {
            schemaVersion: 1 as const,
            windowSequence: this.windowSequence,
            pageHidePersisted: persisted,
            pageHideCount: this.pageHideCount,
            durationMs,
            attemptBufferLimit: MAX_ATTEMPT_DETAILS,
            attemptsDropped: this.attemptsDropped,
            attemptTransitionsDropped: this.attemptTransitionsDropped,
            searchKeysDropped: this.searchKeysDropped,
            adoptionsDropped: this.adoptionsDropped,
            completedWithoutDisplayAtPagehide,
            completedNotAdoptedAtPagehide,
            bySource: this.counts,
        };
        const estimatedPropertiesBytes = new TextEncoder().encode(
            JSON.stringify(base),
        ).byteLength;
        const summary = { ...base, estimatedPropertiesBytes };
        this.counts = emptySourceCounts();
        this.attemptsDropped = 0;
        this.attemptTransitionsDropped = 0;
        this.searchKeysDropped = 0;
        this.adoptionsDropped = 0;
        this.windowStartedAt = this.now();
        return summary;
    }

    pageShow(persisted: boolean) {
        if (persisted) this.windowSequence += 1;
    }

    private expireUnused() {
        const now = this.now();
        for (const attempt of this.attempts) {
            if (
                attempt.readyAt === undefined ||
                attempt.adopted ||
                attempt.observations.includes("expired_unused") ||
                now - attempt.readyAt < PREFETCH_STALE_TIME_MS
            ) {
                continue;
            }
            this.observe(attempt, "expired_unused");
        }
        for (const attempt of this.overflowById.values()) {
            if (
                attempt.readyAt === undefined ||
                attempt.adopted ||
                attempt.observations.has("expired_unused") ||
                now - attempt.readyAt < PREFETCH_STALE_TIME_MS
            ) {
                continue;
            }
            this.observeOverflow(attempt, "expired_unused");
        }
    }

    private count(
        source: SearchPrefetchSource,
        observation: SearchPrefetchObservation,
    ) {
        this.counts[source][observation] += 1;
    }

    private observe(
        attempt: PrefetchAttempt,
        observation: SearchPrefetchObservation,
    ) {
        if (attempt.observations.includes(observation)) return;
        attempt.observations.push(observation);
        this.count(attempt.source, observation);
    }

    private observeOverflow(
        attempt: {
            source: SearchPrefetchSource;
            observations: Set<SearchPrefetchObservation>;
        },
        observation: SearchPrefetchObservation,
    ) {
        if (attempt.observations.has(observation)) return;
        attempt.observations.add(observation);
        this.count(attempt.source, observation);
    }
}

export const searchPrefetchTracker = new SearchPrefetchTracker();
